import * as helpers from '@nha/functions';
import { differenceInMinutes, differenceInSeconds } from 'date-fns';

/**
 * Helper class to decode and find JWT expiration.
 * All credit goes to angular2-jwt since it was ripped from there
 * https://github.com/auth0/angular2-jwt/blob/master/angular2-jwt.ts
 */

function decodeToken(token: string): any {
	const parts = token.split('.');

	if (parts.length !== 3) {
		throw new Error('JWT must have 3 parts');
	}

	const decoded = helpers.urlBase64Decode(parts[1]);
	if (!decoded) {
		throw new Error('Cannot decode the token');
	}

	return JSON.parse(decoded);
}

function getTokenExpirationDate(token: string): Date {
	const decoded: any = decodeToken(token);

	if (!decoded.hasOwnProperty('exp')) {
		return null;
	}

	const date = new Date(0); // The 0 here is the key, which sets the date to the epoch
	date.setUTCSeconds(decoded.exp);

	return date;
}

function getTokenExpirationSecondsFromNow(token: string, offsetSeconds?: number) {
	offsetSeconds = offsetSeconds || 0;

	const date: Date = getTokenExpirationDate(token);
	return differenceInSeconds(date, new Date().valueOf() + offsetSeconds * 1000);
}

function getTokenExpirationMinutesFromNow(token: string, offsetSeconds?: number) {
	offsetSeconds = offsetSeconds || 0;

	const date: Date = getTokenExpirationDate(token);
	return differenceInMinutes(date, new Date().valueOf() + offsetSeconds * 1000);
}

function isTokenExpired(token: string, offsetSeconds?: number): boolean {
	const date = getTokenExpirationDate(token);
	offsetSeconds = offsetSeconds || 0;

	if (date == null) {
		return false;
	}

	// Token expired?
	return !(date.valueOf() > new Date().valueOf() + offsetSeconds * 1000);
}

function getTokenValue(token: string, field: string): string | number {
	const decodedToken: any = decodeToken(token);
	return decodedToken[field];
}

export { decodeToken, getTokenExpirationDate, getTokenExpirationSecondsFromNow, getTokenExpirationMinutesFromNow, isTokenExpired, getTokenValue };
